/**
 * What the save control's script asks `/user/ci/words/`, and what comes back.
 *
 * Apart from `save-word.ts`, which is the page and the presses on it. This half
 * is the two requests and the shape of an answer, and it is separate because a
 * browser has to treat what it fetched as unknown until it has looked. Every
 * field below is read one at a time rather than trusted.
 *
 * The route is the handler's `CiWords`, and `src/user/ci-words.ts` says why it
 * answers JSON rather than a page.
 */

/** Where a reader's words live, and where a press without a script goes. */
export const ciPath = "/user/ci/";

/** Where the script asks about words and posts one. */
const ciWordsPath = "/user/ci/words/";

/** Where the browser extension asks for every headword the reader holds. */
const ciHeadwordsPath = "/user/ci/words/all/";

/** The parameter handing back the stamp the last answer carried. */
const knownStampParameter = "known";

/** The field carrying the headword, on the form and on the query string. */
export const wordField = "word";

/** The optional tag carried by a save from the segmenter. */
export const tagField = "tag";

/**
 * The field saying which way a press goes, which `CiWords.save` reads.
 *
 * A page can be older than the reader's list. Asking for what the button
 * offers saves a word another tab saved already, where a toggle would remove
 * it.
 */
const intentField = "intent";

/** Which way a press goes, as the button drew it. */
export type SaveIntent = "save" | "remove";

/** The parameter carrying the words a page is asking about. */
const wordsParameter = "words";

/** What separates them, which a headword can never carry. */
const wordsSeparator = ",";

/** DynamoDB's maximum number of keys in one BatchGetItem request. */
const wordsPerRequest = 100;

/** Keep a long segmenter's requests from opening every connection at once. */
const requestsAtOnce = 4;

/** The parameter naming a word the cap would not let through. */
const refusedParameter = "full";

/** What the route answers, before anything has looked at it. */
interface WordsAnswer {
  readonly saved?: unknown;
  readonly full?: unknown;
  readonly milestone?: unknown;
}

/** What the list route answers, before anything has looked at it. */
interface HeadwordsAnswer {
  readonly saved?: unknown;
  readonly stamp?: unknown;
  readonly unchanged?: unknown;
}

/**
 * Every headword a reader holds, as the list route answered.
 *
 * Kept by the caller between pages, and handed back as `known` so the route
 * can say nothing moved without sending the words again.
 */
export interface SavedHeadwordList {
  /** The headwords. */
  readonly words: ReadonlySet<string>;

  /** What to hand back next time, where the route gave one. */
  readonly stamp?: string;
}

/** What one press did, as the page reads it. */
export interface PressResult {
  /** Whether the reader now holds the word. */
  readonly held: boolean;

  /** Whether the reader has no session, and is to be offered an account. */
  readonly signedOut: boolean;

  /** Whether the save was refused at the cap. */
  readonly full: boolean;

  /**
   * The line a free learner's save is congratulated with, as markup the
   * handler drew, where this save reached a milestone (CB-61).
   */
  readonly milestone?: string;
}

/** Where a reader is sent when the cap would not let a word through. */
export function refusedLocation(word: string): string {
  return `${ciPath}?${refusedParameter}=${encodeURIComponent(word)}`;
}

/**
 * Which of the words on this page the reader holds.
 *
 * Nothing where the answer is not a list of words, which is a reader with no
 * session and anything else this could not read. The page draws every control
 * offering to save, so an answer nobody understood leaves it as it is.
 *
 * **`onSignedOut` separates those two for a caller that keeps a credential.**
 * A page on this site has a cookie it never sees and a session that lapsed
 * between the draw and the ask costs it nothing, every control being drawn
 * offering to save either way. The extension holds a session id of its own and
 * presents it on every request, so a dead one leaves it showing a connected
 * account it can do nothing with until something says otherwise. This is what
 * says so. See `SavedWords.held` in `src/extension/`.
 * @param fetched - What asks the route.
 * @param words - The words the page is asking about.
 * @param onSignedOut - Called where the route says there is nobody to ask
 *   about, which it answers as `saved: null`.
 * @returns The words the reader holds, or nothing where that could not be read.
 */
export async function askHeldWords(
  fetched: typeof fetch,
  words: readonly string[],
  onSignedOut?: () => void,
): Promise<readonly string[] | undefined> {
  const distinct = [...new Set(words)];
  const batches = Array.from(
    { length: Math.ceil(distinct.length / wordsPerRequest) },
    (_, at) => distinct.slice(at * wordsPerRequest, (at + 1) * wordsPerRequest),
  );
  const held: string[] = [];
  let next = 0;

  const ask = async (): Promise<boolean> => {
    while (next < batches.length) {
      // The loop bound proves this index is present.
      const batch = batches[next] as readonly string[];
      next += 1;

      // oxlint-disable-next-line no-await-in-loop -- each worker keeps at most one request open and takes the next batch when it finishes
      const answer = await fetched(
        `${ciWordsPath}?${wordsParameter}=${encodeURIComponent(batch.join(wordsSeparator))}`,
      );
      // oxlint-disable-next-line no-await-in-loop -- the response body belongs to the request immediately above
      const saved = ((await answer.json()) as WordsAnswer).saved;

      if (saved === null) {
        onSignedOut?.();

        return false;
      }

      if (!Array.isArray(saved)) {
        return false;
      }

      held.push(...(saved as readonly string[]));
    }

    return true;
  };

  const understood = await Promise.all(
    Array.from({ length: Math.min(requestsAtOnce, batches.length) }, ask),
  );

  return understood.every(Boolean) ? held : undefined;
}

/**
 * Every headword the reader holds, asked in one request.
 *
 * For a caller asking about more words than `askHeldWords` should be sent, the
 * browser extension above all, which asks about every word on a page. The
 * route reads one item rather than a batch per hundred words. See
 * `CiHeadwords` in `src/user/`.
 *
 * **A list held from before is handed back by its stamp.** Where nothing has
 * moved the route answers without the words and this answers with the list it
 * was given. Where something has, or the stamp is unknown to the route, the
 * whole list comes back.
 * @param fetched - What asks the route.
 * @param held - The list from the last answer, if the caller kept one.
 * @param onSignedOut - Called where the route says there is nobody to ask
 *   about, which it answers as `saved: null`.
 * @returns The list, or nothing where the answer could not be read.
 */
export async function askSavedHeadwords(
  fetched: typeof fetch,
  held?: SavedHeadwordList,
  onSignedOut?: () => void,
): Promise<SavedHeadwordList | undefined> {
  const asked =
    held?.stamp === undefined
      ? ciHeadwordsPath
      : `${ciHeadwordsPath}?${knownStampParameter}=${encodeURIComponent(held.stamp)}`;
  const answer = await headwordsAnswer(await fetched(asked));

  if (answer === undefined) {
    return undefined;
  }

  const stamp = typeof answer.stamp === "string" ? answer.stamp : undefined;

  if (answer.saved === null) {
    onSignedOut?.();

    return undefined;
  }

  if (answer.unchanged === true && held !== undefined) {
    return held;
  }

  if (typeof answer.saved !== "string") {
    return undefined;
  }

  return {
    words: new Set(
      answer.saved.split(wordsSeparator).filter((word) => word !== ""),
    ),
    ...(stamp !== undefined && { stamp }),
  };
}

/**
 * The route's answer, or nothing where the body is not JSON.
 *
 * An error page from CloudFront or the Function's runtime is HTML or plain
 * text, and `askSavedHeadwords` answers that as an answer it could not read.
 */
async function headwordsAnswer(
  response: Response,
): Promise<HeadwordsAnswer | undefined> {
  try {
    return (await response.json()) as HeadwordsAnswer;
  } catch {
    return undefined;
  }
}

/**
 * Save or remove one word, and say what happened to it.
 *
 * Without an intent the route turns the word over, which is what the browser
 * extension asks for.
 */
export async function toggleSavedWord(
  fetched: typeof fetch,
  word: string,
  tag?: string,
  intent?: SaveIntent,
): Promise<PressResult> {
  const fields = new URLSearchParams({ [wordField]: word });
  if (intent !== undefined) {
    fields.set(intentField, intent);
  }
  if (tag !== undefined) {
    fields.set(tagField, tag);
  }

  const answer = await fetched(ciWordsPath, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: fields.toString(),
  });

  const reported = (await answer.json()) as WordsAnswer;
  const saved = reported.saved;

  return {
    held: Array.isArray(saved) && saved.includes(word),
    signedOut: !Array.isArray(saved),
    full: reported.full === true,
    ...(typeof reported.milestone === "string" && {
      milestone: reported.milestone,
    }),
  };
}
